/* Comparison landers: carousel, how-to scroller, timeline progress, FAQ accordion */
(function () {
  var REDUCED = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* Looping, transform-based carousel: N slides per page, cloned slides at both ends, autoplay, dots, swipe. */
  class CompareCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.viewport = this.querySelector('[data-viewport]');
      this.track = this.querySelector('[data-track]');
      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.live = this.querySelector('[data-live]');
      this.count = this.cards.length;
      if (!this.viewport || !this.track || this.count < 2) return;

      this.index = 0;
      this.target = 0;
      this.autoplayMs = parseInt(this.dataset.autoplay, 10) || 0;
      this.loop = this.dataset.loop !== 'false';
      this.minWidth = parseInt(this.dataset.minWidth, 10) || 0;

      if (this.loop) this.buildClones();
      this.layout = this.layout.bind(this);
      this.onTransitionEnd = this.onTransitionEnd.bind(this);
      this.track.addEventListener('transitionend', this.onTransitionEnd);
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.layout);
        this.ro.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.layout);
      }
      if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
      if (this.next) this.next.addEventListener('click', () => this.step(1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
      this.bindSwipe();
      this.addEventListener('pointerenter', () => { this.hover = true; });
      this.addEventListener('pointerleave', () => { this.hover = false; });
      this.addEventListener('focusin', () => { this.focused = true; });
      this.addEventListener('focusout', () => { this.focused = false; });
      this.layout();
      this.startAutoplay();
    }

    disconnectedCallback() {
      if (this.timer) clearInterval(this.timer);
      if (this.ro) this.ro.disconnect();
      window.removeEventListener('resize', this.layout);
    }

    buildClones() {
      var make = function (card) {
        var clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-clone', '');
        clone.removeAttribute('data-card');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('a, button, [tabindex]').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
        return clone;
      };
      var before = document.createDocumentFragment();
      var after = document.createDocumentFragment();
      this.cards.forEach(function (card) { before.appendChild(make(card)); after.appendChild(make(card)); });
      this.track.insertBefore(before, this.track.firstChild);
      this.track.appendChild(after);
    }

    metrics() {
      var style = getComputedStyle(this.track);
      var gap = parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
      return { w: this.cards[0].getBoundingClientRect().width, gap: gap };
    }

    offsetFor(t) {
      var m = this.metrics();
      return -((this.loop ? this.count : 0) + t) * (m.w + m.gap);
    }

    setTransform(x, animate) {
      this.track.style.transition = animate && !REDUCED ? 'transform 0.4s ease' : 'none';
      this.track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
      if (!animate || REDUCED) void this.track.offsetWidth;
    }

    layout() {
      if (this.minWidth && window.innerWidth < this.minWidth) {
        this.inactive = true;
        this.track.style.transition = 'none';
        this.track.style.transform = '';
        this.updateUI();
        return;
      }
      this.inactive = false;
      this.target = this.index;
      this.setTransform(this.offsetFor(this.index), false);
      this.updateUI();
    }

    goTo(t, animate) {
      if (animate === undefined) animate = true;
      if (this.inactive) return;
      if (!this.loop) t = Math.max(0, Math.min(t, this.count - 1));
      if (this.target >= this.count || this.target < 0) this.normalise();
      this.target = t;
      this.index = ((t % this.count) + this.count) % this.count;
      this.setTransform(this.offsetFor(t), animate);
      this.updateUI();
      if (!animate || REDUCED) this.normalise();
    }

    step(dir) { this.goTo(this.target + dir); }

    onTransitionEnd(e) { if (e.target === this.track) this.normalise(); }

    normalise() {
      if (this.target === this.index) return;
      this.target = this.index;
      this.setTransform(this.offsetFor(this.index), false);
    }

    updateUI() {
      var i = this.index;
      this.dots.forEach(function (dot, d) { dot.setAttribute('aria-current', d === i ? 'true' : 'false'); });
      if (this.live) this.live.textContent = 'Slide ' + (i + 1) + ' of ' + this.count;
    }

    startAutoplay() {
      if (!this.autoplayMs || REDUCED) return;
      var self = this;
      this.timer = setInterval(function () {
        if (self.hover || self.focused || document.hidden) return;
        self.step(1);
      }, this.autoplayMs);
    }

    bindSwipe() {
      var x0 = null, y0 = null, self = this;
      this.viewport.addEventListener('touchstart', function (e) {
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
      }, { passive: true });
      this.viewport.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        var dy = e.changedTouches[0].clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) self.step(dx < 0 ? 1 : -1);
      }, { passive: true });
    }
  }

  /* Native scroll-snap scroller with dots/arrows (how-to steps). */
  class CompareScroller extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-viewport]');
      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      if (!this.viewport || !this.cards.length) return;

      this.onScroll = this.onScroll.bind(this);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
      if (this.next) this.next.addEventListener('click', () => this.step(1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
      this.onScroll();
    }

    disconnectedCallback() {
      if (this.viewport) this.viewport.removeEventListener('scroll', this.onScroll);
    }

    currentIndex() {
      const left = this.viewport.scrollLeft;
      let best = 0;
      let bestDist = Infinity;
      this.cards.forEach((card, i) => {
        const dist = Math.abs(card.offsetLeft - this.viewport.offsetLeft - left);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      return best;
    }

    goTo(index) {
      const i = Math.max(0, Math.min(index, this.cards.length - 1));
      const card = this.cards[i];
      this.viewport.scrollTo({ left: card.offsetLeft - this.viewport.offsetLeft, behavior: 'smooth' });
    }

    step(dir) { this.goTo(this.currentIndex() + dir); }

    onScroll() {
      const i = this.currentIndex();
      const maxLeft = this.viewport.scrollWidth - this.viewport.clientWidth - 2;
      this.dots.forEach((dot, d) => dot.setAttribute('aria-current', d === i ? 'true' : 'false'));
      if (this.prev) this.prev.disabled = this.viewport.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.viewport.scrollLeft >= maxLeft;
    }
  }


  /* Timeline scroll progress: the line grows and the current step gets an `in-view` class as the page scrolls. */
  function initTimeline(section) {
    if (section.__progress) return;
    var items = Array.from(section.querySelectorAll('[data-progressblock]'));
    var lineToDraw = section.querySelector('[data-draw-line]');
    if (!lineToDraw || !items.length) return;
    section.__progress = true;
    var winSize = window.innerWidth;
    var main = section.closest('main') || document.querySelector('main') || document.body;
    function onScroll() {
      var windowDistance = window.pageYOffset;
      var windowHeight = window.innerHeight / 2;
      var timelineDistance = section.getBoundingClientRect().top - main.getBoundingClientRect().top;
      items.forEach(function (item) { item.classList.remove('in-view'); });
      if (windowDistance < timelineDistance - windowHeight) return;
      var isMobile = winSize <= 767;
      var line = isMobile
        ? windowDistance - timelineDistance - windowHeight / 2.7
        : windowDistance - timelineDistance + windowHeight;
      lineToDraw.style.height = line + 'px';
      if (line <= 0) return;
      var active = null;
      items.forEach(function (item) {
        if (line >= item.offsetTop + (isMobile ? 30 : 15)) active = item;
      });
      if (active) active.classList.add('in-view');
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { winSize = window.innerWidth; onScroll(); });
    onScroll();
  }
  function initTimelines() { document.querySelectorAll('[data-progress-box]').forEach(initTimeline); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initTimelines); else initTimelines();
  document.addEventListener('shopify:section:load', initTimelines);


  /* FAQ accordion with animated height */
  function initFaq(root) {
    if (root.__faq) return;
    root.__faq = true;
    root.querySelectorAll('[data-faq-toggle]').forEach(function (btn) {
      var item = btn.closest('[data-faq-item]');
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!item || !panel) return;
      function open() {
        item.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
        panel.removeAttribute('aria-hidden');
        panel.style.height = '0px';
        void panel.offsetHeight;
        panel.style.height = panel.scrollHeight + 'px';
        panel.addEventListener('transitionend', function done(e) {
          if (e.target !== panel) return;
          panel.removeEventListener('transitionend', done);
          if (item.classList.contains('is-open')) panel.style.height = 'auto';
        });
      }
      function close() {
        item.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');
        panel.style.height = panel.scrollHeight + 'px';
        void panel.offsetHeight;
        panel.style.height = '0px';
      }
      btn.addEventListener('click', function () {
        if (btn.getAttribute('aria-expanded') === 'true') close(); else open();
      });
    });
  }
  function initFaqs() { document.querySelectorAll('[data-compare-faq]').forEach(initFaq); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initFaqs); else initFaqs();
  document.addEventListener('shopify:section:load', initFaqs);

  if (!customElements.get('compare-reviews')) customElements.define('compare-reviews', CompareCarousel);
  if (!customElements.get('compare-steps')) customElements.define('compare-steps', CompareCarousel);
})();
